import React, { useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import {
  lists,
  FILTER_TYPE_MULTI_RANGE,
  FILTER_TYPE_SLIDER,
  FILTER_TYPE_DATE_PRESETS,
  FILTER_TYPE_CHECKBOX,
  FILTER_TYPE_RADIO,
  FILTER_TYPE_CHECKBOX_BOOLEAN,
} from "../../utils/constants/constants";
import FilterItemDropdown from "./FilterItemDropdown";
import GeneralFilters from "./GeneralFilters";
import DateRangeFilter from "./DateRangeFilter";
import SingleDate from "./SingleDateFilter";
import MultiRangeFilter from "../../UI/RangeSlider/MultiRangeFilter";
import ValueSliderFilter from "../../UI/RangeSlider/ValueSliderFilter";
import { sortMenuFilters } from "../../utils/menuFilters";
import DatePresets from "./MenuFiltersDatePresets/DatePresets";

const Container = styled.div(
  ({ isLoading }) => `
  font-size: 1em;
  ${
    isLoading &&
    `
      pointer-events: none;
      opacity: 0.6;
    `
  }; 
`
);

export default function Filters(props) {
  const {
    dataSourceAccessConfig,
    menuFilterShortList,
    filterType,
    select,
    comparisonModeItem,
    selectPowerEditorModeFilter,
    additionalTheme,
    searchFilterLoading,
    selectedValues,
    loadValues,
    currentTabId,
    error,
    searchValue,
    setSearchValue,
    likeEnabled,
    setLikeEnabled,
    domain,
  } = props;
  const { type, uuid } = filterType ?? {};
  const { includedEmptyValues = {} } = additionalTheme ?? {};

  const whitelistedValues =
    dataSourceAccessConfig.dataSourceFieldValues.byUuid[
      filterType.dataSourceFieldUuid
    ]?.values ?? [];

  const whitelistedValuesFiltered = whitelistedValues.length
    ? filterType.values.filter((value) =>
        whitelistedValues.includes(value.value)
      )
    : filterType.values;

  const elementsSorted = sortMenuFilters(whitelistedValuesFiltered);

  // replace falsy label with "Empty" if allowed in site configuration
  const setEmptyIfAllowed = useCallback(
    (filter) => {
      if (filterType.showEmptyValues) {
        return filter;
      }
      if (!filter.label && includedEmptyValues[uuid]) {
        return { ...filter, label: "Empty" };
      }

      return filter;
    },
    [filterType.showEmptyValues, includedEmptyValues, uuid]
  );

  // search filters if they are to many
  const filterMap = useCallback(
    ({ label, checked }) => {
      if (!searchValue) {
        return true;
      }
      return (
        label?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
        checked
      );
    },
    [searchValue]
  );

  const elementsFiltered = useMemo(() => {
    if (filterType.totalCount > elementsSorted.length) {
      return elementsSorted;
    }

    return elementsSorted.map(setEmptyIfAllowed).filter(filterMap);
  }, [elementsSorted, filterMap, filterType.totalCount, setEmptyIfAllowed]);

  // make a test for sorted

  const finalFiltersBeforePagination = useMemo(() => {
    if (!Array.isArray(menuFilterShortList)) {
      return elementsFiltered;
    }
    return elementsFiltered.filter((v) =>
      menuFilterShortList.includes(v.value)
    );
  }, [elementsFiltered, menuFilterShortList]);

  const isList = lists.some((l) => l === type);

  const filtersWithSelect = isList ? (
    <FilterItemDropdown
      filters={finalFiltersBeforePagination}
      select={select}
      type={type}
      searchFilterLoading={searchFilterLoading}
      filter={filterType}
      additionalTheme={additionalTheme}
    />
  ) : null;

  const multiRangeSlider =
    type === FILTER_TYPE_MULTI_RANGE ? (
      <MultiRangeFilter
        onChange={select}
        filter={props.filterType}
        domain={domain}
      />
    ) : null;

  // @todo add FILTER_TYPE_SLIDER
  const slider =
    type === FILTER_TYPE_SLIDER ? (
      <ValueSliderFilter
        select={select}
        onChange={select}
        filter={props.filterType}
        value={finalFiltersBeforePagination}
        additionalTheme={additionalTheme}
      />
    ) : null;

  const presets =
    type === FILTER_TYPE_DATE_PRESETS ? (
      <DatePresets
        select={select}
        type={type}
        defaultValues={filterType.defaultValues}
        currentTabId={currentTabId}
        filterType={filterType}
      />
    ) : null;

  const GENERAL_FILTERS_TYPE =
    type === FILTER_TYPE_CHECKBOX ||
    type === FILTER_TYPE_RADIO ||
    type === FILTER_TYPE_CHECKBOX_BOOLEAN;

  return (
    <Container isLoading={searchFilterLoading && !isList}>
      {filtersWithSelect}
      {multiRangeSlider}
      {slider}
      {presets}

      <DateRangeFilter select={select} type={type} filterType={filterType} />
      <SingleDate select={select} type={type} filterType={filterType} />

      {GENERAL_FILTERS_TYPE ? (
        <GeneralFilters
          setSearchValue={setSearchValue}
          select={select}
          finalFiltersBeforePagination={finalFiltersBeforePagination}
          type={type}
          comparisonModeItem={comparisonModeItem}
          selectPowerEditorModeFilter={selectPowerEditorModeFilter}
          filter={filterType}
          searchFilterLoading={searchFilterLoading}
          additionalTheme={additionalTheme}
          selectedValues={selectedValues}
          loadValues={loadValues}
          error={error}
          searchValue={searchValue}
          likeEnabled={likeEnabled}
          setLikeEnabled={setLikeEnabled}
        />
      ) : null}
    </Container>
  );
}
